// tslint:disable:max-line-length
import { action, NetbankingAPI } from "aaa";
import { flatMap, isEqual } from "lodash";
import { computed, observable, toJS } from "mobx";
import { GenericAPIClient, NetbankingAPIClient } from "network/APIClient";
import { AccountsStore } from "stores/AccountsStore";
import { CardsStore } from "stores/CardsStore";
import { addressToString, ContactsStore, phoneNumberToString } from "stores/ContactsStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { IBuildingsAccount } from "types/account";
import { ISigningInfo } from "types/signing";
import { ISurveyRequest } from "types/survey";

export type ISurveyOption = {
    value: string;
    label: string;
    key?: string;
};

type GiroProduct = {
    product: "GIRO",
    productType: "CURRENT" | "LOAN"
};

type SavingsProduct = {
    product: "SAVINGS",
    productType: "SAVINGS"
};

type BuildingSavingsProduct = {
    product: "BUILDING_SAVINGS",
    productType: "BUILD_SAVING"
};

type CardProduct = {
    product: "BRAND_INDEPENDENT",
    productType: "CREDIT" | "DEBIT" | "PREPAID" | "INDOOR",
    characteristics?: "MAIN" | "AUTHORIZED" | "AUTHORIZED_WITH_STANDALONE_DEBIT_ACCOUNT" | "BUSINESS" | "SAVINGS" | "OTHER"
};

type MastercardProduct = Omit<CardProduct, "product"> & {
    product: "MASTERCARD",
};

type VisaProduct = Omit<CardProduct, "product"> & {
    product: "VISA",
};

type HomeDetails = {
    product: "HOME",
    productType: "ADDRESS" | "EMAIL" | "PHONE"
};

type TextInput = {
    product?: never,
    productType?: never
};

type SurveyFormComponentDefinition = {
    id: string;
    component: "selection" | "textInput";
    label?: string | null;
    placeholder?: string | null;
    value?: string | null | string[];
    // product?: "GIRO" | "SAVINGS" | "BRAND_INDEPENDENT" | "HOME"; // what are all possible values?
    // productType?: "CURRENT" | "LOAN" | "SAVINGS" | "CREDIT" | "ADDRESS" | "EMAIL" | "PHONE"; // what are all possible values?
    // characteristics?: "MAIN", // what are all possible values?
    subType?: string | null;
    flags?: string[];
    options?: {
        value: string;
        label: string;
        key?: string | undefined; // sanity type for empty dropdown option
    }[] | null;
    constraints?: {
        mandatoryType: "OPTIONAL" | "MANDATORY_ALWAYS";
        visibilityExpression?: string | null;
        accessExpression?: string | null;
    };
    displayMode?: "RADIO_BUTTONS" | "CHECKBOXES" | "DROPDOWN";
};

type SurveyFormComponentItemDefinition = SurveyFormComponentDefinition & (
    GiroProduct |
    SavingsProduct |
    BuildingSavingsProduct |
    CardProduct |
    MastercardProduct |
    VisaProduct |
    HomeDetails |
    TextInput
);

type SurveyComponentDefintion = {
    id?: string;
    component: "headline" | "card" | "form" | "image";
    alwaysShow?: boolean;
    title?: string | null;
    titleAlignment?: "LEFT" | "CENTER" | "RIGHT"; // what are all possible values?
    text?: string;
    textAlignment?: "LEFT" | "CENTER" | "RIGHT"; // what are all possible values?
    action?: unknown; // what are possible values?
    actions?: {
        signing: "sID/CardTAN signing" | "None";
        postActions?: ("sendToLUX" | "documentInDMS")[];
    };
    items?: SurveyFormComponentItemDefinition[];
    image?: {
        path: string;
        width?: number;
        height?: number;
        ratio?: number;
    },
    imageAlignment?: "LEFT" | "CENTER" | "RIGHT"; // what are all possible values?
};

type SurveyDefinition = {
    body: SurveyComponentDefintion[];
};

const mockJson: SurveyDefinition = {
    "body": [
        {
            "component": "headline",
            "title": "Checkliste für Ihre Betriebsübergabe",
            "alwaysShow": false,
            "action": null
        },
        {
            "component": "card",
            "title": null,
            "titleAlignment": "LEFT",
            "text": "<p>Versuchen Sie für sich selbst herauszufinden, was Ihnen bei der Betriebsübergabe wichtig ist, welche Punkte noch geklärt bzw. welche anstehenden Entscheidungen getroffen werden müssen.</p>\r\n",
            "textAlignment": "LEFT"
        },
        // {
        //     "component": "image",
        //     "title": null,
        //     "titleAlignment": "LEFT",
        //     "imageAlignment": "LEFT",
        //     "image": {
        //         "path": "https://d3av3h57bg5uxz.cloudfront.net/gemlip/v1/dam/2bU9ZcL7pF695w6FXYfbpinTBVh4/at/spk-sgruppe/common/kit/(size)_0_0_0_0_0_m_1675426171184.creditcard.png(webp)",
        //         "width": 1696,
        //         "height": 580,
        //         "ratio": 2.924
        //     }
        // },
        {
            "component": "headline",
            "title": "Zu Ihrer Person",
            "alwaysShow": false,
            "action": null
        },
        {
            "component": "form",
            "actions": {
                "signing": "sID/CardTAN signing",
                "postActions": [
                    "sendToLUX"
                ]
            },
            "items": [
                {
                    "component": "selection",
                    "id": "1.1",
                    "label": "Haben Sie sich bereits Gedanken über Ihre Nachfolge im Unternehmen gemacht?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Ja",
                            "value": "1.1.1"
                        },
                        {
                            "label": "Nein",
                            "value": "1.1.2"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "RADIO_BUTTONS"
                },
                {
                    "component": "selection",
                    "id": "1.2",
                    "label": "Haben Sie den Zeitpunkt Ihrer geplanten Übergabe bereits fixiert?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Ja",
                            "value": "1.2.1"
                        },
                        {
                            "label": "Nein",
                            "value": "1.2.2"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "RADIO_BUTTONS"
                },
                {
                    "component": "textInput",
                    "id": "1.3",
                    "label": "Wann ist die Übergabe geplant?",
                    "placeholder": "Bitte hier Ihre Antwort eingeben",
                    "value": null,
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    }
                },
                {
                    "component": "selection",
                    "id": "1.4",
                    "label": "Planen Sie einen kompletten Rückzug oder eine stufenweise Übergabe?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Kompletter Rückzug",
                            "value": "1.4.1"
                        },
                        {
                            "label": "Stufenweise Übergabe",
                            "value": "1.4.2"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "RADIO_BUTTONS"
                },
                {
                    "component": "selection",
                    "id": "1.5",
                    "label": "Wollen Sie nach der Übergabe noch Einfluss auf das Unternehmen ausüben?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Nein",
                            "value": "1.5.1"
                        },
                        {
                            "label": "Ja",
                            "value": "1.5.2"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "RADIO_BUTTONS"
                },
                {
                    "component": "textInput",
                    "id": "1.5.2.1",
                    "label": "Im Fall von Ja, tragen Sie bitte den gewünschten Einfluss auf das Unternehmen ein: ",
                    "placeholder": "Bitte hier Ihre Antwort eingeben",
                    "value": null,
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    }
                },
                {
                    "component": "selection",
                    "id": "1.6",
                    "label": "Welche Ziele verfolgen Sie mit einer Übergabe?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Der Bestand des Unternehmens muss gesichert werden.",
                            "value": "1.6.1"
                        },
                        {
                            "label": "Die Arbeitsplätze dürfen nicht gefährdet werden.",
                            "value": "1.6.2"
                        },
                        {
                            "label": "Eine angemessene Altersvorsorge muss gewährleistet sein.",
                            "value": "1.6.3"
                        },
                        {
                            "label": "Meine Familie muss ein Einkommen haben.",
                            "value": "1.6.4"
                        },
                        {
                            "label": "Das Familienvermögen muss gesichert werden.",
                            "value": "1.6.5"
                        },
                        {
                            "label": "Die Nachfolgeregelung sollte steuerlich und erbrechtlich möglichst günstig sein.",
                            "value": "1.6.6"
                        },
                        {
                            "label": "Die finanzielle Situation der NachfolgerIn darf nicht überstrapaziert werden.",
                            "value": "1.6.7"
                        },
                        {
                            "label": "Andere/weitere:",
                            "value": "1.6.8"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "CHECKBOXES"
                },
                {
                    "component": "textInput",
                    "id": "1.6.8.1",
                    "label": "Im Fall von \"Andere/weitere\", teilen Sie mit uns Ihre Ziele:",
                    "placeholder": "Bitte hier Ihre Antwort eingeben",
                    "value": null,
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    }
                },
                {
                    "component": "selection",
                    "id": "1.7",
                    "label": "Haben Sie Ihre Altersvorsorge geklärt?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Ja",
                            "value": "1.7.1"
                        },
                        {
                            "label": "Nein",
                            "value": "1.7.2"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "RADIO_BUTTONS"
                }
            ]
        },
        {
            "component": "headline",
            "title": "Die Nachfolger:in",
            "alwaysShow": false,
            "action": null
        },
        {
            "component": "form",
            "actions": {
                "signing": "None",
                "postActions": [
                    "sendToLUX"
                ]
            },
            "items": [
                {
                    "component": "selection",
                    "id": "2.1",
                    "label": "Ist bereits eine geeignete Nachfolger:in gefunden worden?",
                    "placeholder": null,
                    "value": null,
                    "options": [
                        {
                            "label": "Ja",
                            "value": "2.1.1"
                        },
                        {
                            "label": "Nein - Wenn Sie mit „Nein“ geantwortet haben, lesen Sie beim Kapitel Die Übergabe weiter.",
                            "value": "2.1.2"
                        }
                    ],
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    },
                    "displayMode": "RADIO_BUTTONS"
                },
                {
                    "component": "textInput",
                    "id": "2.1.1.1",
                    "label": "Wer ist die Nachfolgerin bzw. der Nachfolger (Vorname, Familienname)?",
                    "placeholder": "Bitte hier Ihre Antwort eingeben",
                    "value": null,
                    "constraints": {
                        "mandatoryType": "OPTIONAL",
                        "visibilityExpression": null,
                        "accessExpression": null
                    }
                }
            ]
        }
    ]
};

const mockJson2: SurveyDefinition = {
    body: [{
        component: "headline",
        title: "Finanzplanung",
        alwaysShow: false,
        action: null
    }, {
        component: "card",
        title: "Heute geht es um Ihre Finanzen!",
        titleAlignment: "LEFT",
        text: "<p>Hier steht ein Text um der Kund:in zu erklären warum wir tun was wir tun, damit die Kund:in einen Eindruck bekommt über unser Tun.</p>\r\n",
        textAlignment: "LEFT"
    }, {
        component: "image",
        title: null,
        titleAlignment: "LEFT",
        imageAlignment: "LEFT",
        image: {
            // tslint:disable:max-line-length
            path: "https://d3av3h57bg5uxz.cloudfront.net/gemlip/v1/dam/2bU9ZcL7pF695w6FXYfbpinTBVh4/at/spk-sgruppe/common/kit/(size)_0_0_0_0_0_m_1675426171184.creditcard.png(webp)",
            // path: "https://cdn.pixabay.com/photo/2015/08/23/09/22/banner-902589__340.jpg",
            // tslint:enable:max-line-length
            width: 1696,
            height: 580,
            ratio: 2.924
        },
    }, {
        component: "form",
        actions: {
            signing: "sID/CardTAN signing",
            postActions: [
                "sendToLUX",
                "documentInDMS"
            ]
        },
        items: [{
            component: "selection",
            id: "Q1",
            label: "Unter anderen ein Haushaltsbuch zu fühen. Ich fühle mich.....",
            placeholder: null,
            value: null,
            options: [{
                label: " unter anderen ein Haushaltsbuch zu fühen. Ich fühle mich.....",
                value: "Q1A1"
            }, {
                label: " genaue Planung und ....",
                value: "Q1A2"
            }, {
                label: " aktive Lebensgestaltung ...",
                value: "C"
            }, {
                label: " zu wissen, wieviel",
                value: "D"
            }],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "RADIO_BUTTONS"
        }, {
            component: "selection",
            id: "Q2",
            label: "Sparen bedeutet für mich ...",
            placeholder: null,
            value: null,
            options: [{
                label: "einen Sicherheitspolster ....",
                value: "2A"
            }, {
                label: "dass ich trotzdem...",
                value: "2B"
            }, {
                label: "vor allem Unabhängigkeit ...",
                value: "2C"
            }, {
                label: "Geldverlust. Ich versuche....",
                value: "2D"
            }],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "RADIO_BUTTONS"
        }, {
            component: "selection",
            id: "Q3",
            label: "Geld ist für mich ...",
            placeholder: null,
            value: "3A",
            options: [{
                label: "begrenzt, ich ...",
                value: "3A"
            }, {
                label: "ein Mittel, ...",
                value: "3B"
            }, {
                label: "hart erarbeitet, ....",
                value: "3C"
            }, {
                label: "mein Freund. Ich vermeide ...",
                value: "3D"
            }],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "RADIO_BUTTONS"
        }, {
            component: "selection",
            id: "Q4",
            label: "Meine größte Sorge in Sachen Finanzen ist ...",
            placeholder: null,
            value: "Das ist ein Test",
            options: null,
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "CHECKBOXES"
        }, {
            component: "selection",
            id: "Q5",
            label: "Geld ist für mich ...",
            placeholder: "Bitte wählen Sie aus",
            value: "3B",
            options: [{
                label: "begrenzt, ich ...",
                value: "3A"
            }, {
                label: "ein Mittel, ...",
                value: "3B"
            }, {
                label: "hart erarbeitet, ....",
                value: "3C"
            }, {
                label: "mein Freund. Ich vermeide ...",
                value: "3D"
            }],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "selection",
            id: "Q6",
            label: "Geld ist für mich ...",
            placeholder: null,
            value: "5A",
            options: [{
                label: "Antwort 1",
                value: "5A"
            }, {
                label: "Antwort 2",
                value: "5B"
            }, {
                label: "Antwort 3",
                value: "5C"
            }, {
                label: "Antwort 4",
                value: "5D"
            }],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "textInput",
            id: "ABC",
            label: "Wie gefällt Ihnen George?",
            placeholder: null,
            value: "test initial value",
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            }
        }, {
            component: "selection",
            id: "Accounts",
            label: "Bitte wählen Sie ein Girokonto aus",
            placeholder: null,
            product: "GIRO",
            productType: "CURRENT",
            subType: null,
            flags: [],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "CHECKBOXES"
        }, {
            component: "selection",
            id: "BuldingsAccounts",
            label: "Bitte wählen Sie einen buildings account aus",
            placeholder: null,
            product: "BUILDING_SAVINGS",
            productType: "BUILD_SAVING",
            subType: null,
            flags: [],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "selection",
            id: "SAVINGS",
            label: "Bitte wählen Sie ein Sparkonto aus",
            placeholder: null,
            product: "SAVINGS",
            productType: "SAVINGS",
            flags: [],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "RADIO_BUTTONS"
        }, {
            component: "selection",
            id: "Cards",
            label: "Bitte wählen Sie eine Kreditkarte aus",
            placeholder: null,
            product: "BRAND_INDEPENDENT",
            productType: "CREDIT",
            characteristics: "MAIN",
            flags: [],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "selection",
            id: "Address",
            label: "Bitte wählen Sie eine Adresse aus",
            placeholder: null,
            product: "HOME",
            productType: "ADDRESS",
            flags: ["mainContact"],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "selection",
            id: "EMAIL",
            label: "Bitte wählen Sie eine Email Adresse aus",
            placeholder: null,
            product: "HOME",
            productType: "EMAIL",
            flags: [],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "selection",
            id: "PHONE",
            label: "Bitte wählen Sie eine Handynummer aus, an der wir Sie erreichen können",
            placeholder: null,
            product: "HOME",
            productType: "PHONE",
            flags: [],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "DROPDOWN"
        }, {
            component: "selection",
            id: "Q7",
            label: "Das ist ein Checkbox Test",
            placeholder: null,
            value: null,
            options: [{
                label: "JA",
                value: "YES"
            }, {
                label: "NEIN",
                value: "NO"
            }],
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            },
            displayMode: "CHECKBOXES"
        }, {
            component: "textInput",
            id: "Q10",
            label: "One more question. Give the answer in freetext:",
            placeholder: "Bitte hier Ihre Antwort eingeben",
            value: null,
            constraints: {
                mandatoryType: "OPTIONAL",
                visibilityExpression: null,
                accessExpression: null
            }
        }]
    }]
};

export const isCardSelectionElement = (element: SurveyFormComponentItemDefinition) =>
    element.product === "BRAND_INDEPENDENT" || element.product === "MASTERCARD" || element.product === "VISA";

export const isAccountSelectionElement = (element: SurveyFormComponentItemDefinition) =>
    element.product === "GIRO" || element.product === "SAVINGS";

export const isBuildingsAccountSelectionElement = (element: SurveyFormComponentItemDefinition) =>
    element.product === "BUILDING_SAVINGS";

export const isEmailSelectionElement = (element: SurveyFormComponentItemDefinition) =>
    element.productType === "EMAIL";

export const isPhoneSelectionElement = (element: SurveyFormComponentItemDefinition) =>
    element.productType === "PHONE";

export const isAddressSelectionElement = (element: SurveyFormComponentItemDefinition) =>
    element.productType === "ADDRESS";

export const emptyOption = {
    value: "",
    label: "",
    key: "empty"
};

const accountSubTypeMapping = {
    CURRENT: "CURRENT",
    LOAN: "LOAN",
    SAVINGS: "SAVING"
};

const cardTypeMapping = {
    CREDIT: "CREDIT",
    DEBIT: "BANK_CARD",
    PREPAID: "PREPAID",
    INDOOR: "INDOOR",
};

type ISurveyFormState = {
    [questionId: string]: string | ISurveyOption | ISurveyOption[]
};

// Enable this for local survey testing
const useMock = false;

export class SurveyStore {
    @observable _isLoading = false;
    @observable formDefinition?: SurveyDefinition;
    @observable signInfo?: ISigningInfo;

    @observable formState: ISurveyFormState = {};

    contactsStore = new ContactsStore();
    accountsStore = new AccountsStore();
    cardsStore = new CardsStore();
    @observable buildingsAccounts: IBuildingsAccount[] = [];

    @action async load() {
        this._isLoading = true;

        if (useMock) {
            this.formDefinition = mockJson;
        } else if (
            externalInterfaceStore.urlDecoded && (
                externalInterfaceStore.urlDecoded.startsWith("https://gem.fat.erste-group.net") ||
                externalInterfaceStore.urlDecoded.startsWith("https://gem.fit.erste-group.net") ||
                externalInterfaceStore.urlDecoded.startsWith("https://review.gem.erste-group.net") ||
                externalInterfaceStore.urlDecoded.startsWith("https://www.sparkasse.at") ||
                externalInterfaceStore.urlDecoded.startsWith("https://www.erstebank.at") ||
                externalInterfaceStore.urlDecoded.startsWith("https://emil.fit.sparkasse.at") ||
                externalInterfaceStore.urlDecoded.startsWith("https://emil.fat.sparkasse.at") ||
                externalInterfaceStore.urlDecoded.startsWith("https://emil.stage.erstegroup.com")
            )
        ) {
            this.formDefinition = await GenericAPIClient.requestType<SurveyDefinition>({
                // For local testing we replace the https:// prefix with our proxy url
                url: externalInterfaceStore.urlDecoded.replace("https://", ""),
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                },
                parse: json => json as SurveyDefinition
            });
        } else {
            throw new Error("No or invalid url parameter provided");
        }

        const necessaryLoads = [];

        if (this.hasContactDataSelection) {
            necessaryLoads.push(this.contactsStore?.load());
        }

        if (this.hasAccountSelection) {
            necessaryLoads.push(this.accountsStore?.load());
        }

        if (this.hasCardSelection) {
            necessaryLoads.push(this.cardsStore?.load());
        }

        await Promise.all(necessaryLoads);

        if (this.hasBuildingsAccountSelection) {
            const target = NetbankingAPI.getBuildingsAccounts();
            this.buildingsAccounts = await NetbankingAPIClient.requestType(target);
        }

        const formElements = this.formDefinition?.body.filter(element => element.component === "form");

        formElements?.forEach(formElement => {
            if (formElement?.items && formElement.items.length > 0) {
                let initialState: ISurveyFormState = {};

                formElement.items.forEach(formItem => {
                    // initialState[formItem.id] = "";

                    let options = formItem.options;

                    if (formItem.product === "GIRO" || formItem.product === "SAVINGS") {
                        options = this.getAccountOptions(formItem);
                    }

                    if (formItem.product === "BUILDING_SAVINGS") {
                        options = this.getBuildingsAccountOptions(formItem);
                    }

                    if (formItem.product === "BRAND_INDEPENDENT" || formItem.product === "MASTERCARD" || formItem.product === "VISA") {
                        options = this.getCardOptions(formItem);
                    }

                    if (formItem.productType === "EMAIL") {
                        options = this.getEmailOptions(formItem);
                    }

                    if (formItem.productType === "PHONE") {
                        options = this.getPhoneOptions(formItem);
                    }

                    if (formItem.productType === "ADDRESS") {
                        options = this.getAddressOptions(formItem);
                    }

                    if (formItem.displayMode === "DROPDOWN" || formItem.displayMode === "RADIO_BUTTONS") {
                        if (formItem.value) {
                            initialState[formItem.id] = options?.find(option => option.value === formItem.value) ?? emptyOption;
                        } else {
                            initialState[formItem.id] = emptyOption;
                        }
                    }

                    if (formItem.displayMode === "CHECKBOXES") {
                        initialState[formItem.id] = [];

                        if (Array.isArray(formItem.value) && formItem.value.every(value => typeof value === "string")) {
                            initialState[formItem.id] = flatMap(
                                formItem.value.map(value =>
                                    options?.find(option => option.value === value) ?? []
                                )
                            );
                        }
                    }

                    if (formItem.component === "textInput") {
                        initialState[formItem.id] = "";

                        if (typeof formItem.value === "string") {
                            initialState[formItem.id] = formItem.value;
                        }
                    }
                });

                this.formState = { ...this.formState, ...initialState };
            }
        });

        this._isLoading = false;
    }

    isLoading(): boolean {
        return this._isLoading ||
            !!this.contactsStore?.isLoading ||
            !!this.accountsStore?.isLoading ||
            !!this.cardsStore?.isLoading;
    }

    @computed get hasContactDataSelection(): boolean {
        return this.formDefinition?.body.some(element => element.component === "form" &&
            element.items?.some(formElement =>
                formElement.component === "selection" &&
                formElement.product === "HOME" &&
                (formElement.productType === "EMAIL" || formElement.productType === "PHONE" || formElement.productType === "ADDRESS")
            )) ?? false;
    }

    @computed get hasAccountSelection(): boolean {
        return this.formDefinition?.body.some(element => element.component === "form" &&
            element.items?.some(formElement =>
                formElement.component === "selection" &&
                isAccountSelectionElement(formElement)
            )) ?? false;
    }

    @computed get hasCardSelection(): boolean {
        return this.formDefinition?.body.some(element => element.component === "form" &&
            element.items?.some(formElement =>
                formElement.component === "selection" &&
                isCardSelectionElement(formElement)
            )) ?? false;
    }

    @computed get hasBuildingsAccountSelection(): boolean {
        return this.formDefinition?.body.some(element => element.component === "form" &&
            element.items?.some(formElement =>
                formElement.component === "selection" &&
                isBuildingsAccountSelectionElement(formElement)
            )) ?? false;
    }

    @computed get headline(): string {
        return this.formDefinition?.body.find(element => element.component === "headline")?.title ?? "";
    }

    getEmailOptions = (formElement: SurveyFormComponentDefinition & HomeDetails) => {
        return this.contactsStore?.emailAddresses.filter(emailAddress => (
            (formElement.flags ? formElement.flags?.every(includeFlag => emailAddress.flags?.includes(includeFlag)) : true)
        )).map(emailAddress => ({
            value: emailAddress.id,
            label: emailAddress.email?.email ?? ""
        }));
    }

    getPhoneOptions = (formElement: SurveyFormComponentDefinition & HomeDetails) => {
        return this.contactsStore?.mobilePhones.filter(mobilePhone => (
            (formElement.flags ? formElement.flags?.every(includeFlag => mobilePhone.flags?.includes(includeFlag)) : true)
        )).map(mobilePhone => ({
            value: mobilePhone.id,
            label: phoneNumberToString(mobilePhone)
        }));
    }

    getAddressOptions = (formElement: SurveyFormComponentDefinition & HomeDetails) => {
        return this.contactsStore.addresses.filter(address => (
            (formElement.flags ? formElement.flags?.every(includeFlag => address.flags?.includes(includeFlag)) : true)
        )).map(address => ({
            value: address.id,
            label: addressToString(address)
        }));
    }

    getAccountOptions = (formElement: (SurveyFormComponentDefinition & GiroProduct | SurveyFormComponentDefinition & SavingsProduct)) => {
        return this.accountsStore.accounts.filter(account => (
            account.type === accountSubTypeMapping[formElement.productType] &&
            (formElement.subType ? account.subType === formElement.subType : true) &&
            (formElement.flags ? formElement.flags?.every(includeFlag => account.flags?.includes(includeFlag)) : true)
        )).map(account => ({
            value: account.id,
            label: account.accountno.iban ?? ""
        }));
    }

    getBuildingsAccountOptions = (formElement: SurveyFormComponentDefinition & BuildingSavingsProduct) => {
        return this.buildingsAccounts.filter(account => (
            account.type === formElement.productType &&
            (formElement.flags ? formElement.flags?.every(includeFlag => account.flags?.includes(includeFlag)) : true)
        )).map(account => ({
            value: account.id,
            label: account.accountno.iban ?? ""
        }));
    }

    getCardOptions = (
        formElement: (SurveyFormComponentDefinition & CardProduct) | (SurveyFormComponentDefinition & MastercardProduct) | (SurveyFormComponentDefinition & VisaProduct)
    ) => {
        return this.cardsStore.cards?.filter(card => (
            (formElement.product === "BRAND_INDEPENDENT" ? true : card.brand === formElement.product) &&
            (formElement.productType ? card.type === cardTypeMapping[formElement.productType] : true) &&
            (formElement.characteristics ? card.characteristic === formElement.characteristics : true) &&
            (formElement.flags ? formElement.flags?.every(includeFlag => card.flags?.includes(includeFlag)) : true)
        )).map(card => ({
            value: card.id,
            label: card.number
        })) ?? [];
    }

    @computed get canSubmit(): boolean {
        const formElements = this.formDefinition?.body.filter(element => element.component === "form");

        // check if every mandatory field has a value in the formState
        return formElements?.every(formElement => {
            return formElement.items?.every(formItem => {
                if (formItem.constraints?.mandatoryType === "MANDATORY_ALWAYS") {
                    const formItemState = toJS(this.formState[formItem.id]);

                    if (Array.isArray(formItemState)) {
                        return formItemState.length > 0;
                    } else if (typeof formItemState === "object") {
                        return !isEqual(formItemState, emptyOption);
                    }

                    return !!formItemState;
                }

                return true;
            });
        }) ?? false;
    }

    submitSurvey = async () => {
        const getMappedValues = (
            formElement: SurveyFormComponentItemDefinition,
            mapping: { value: string, label: string } = { value: "value", label: "label" }
        ) => {
            const state = toJS(this.formState[formElement.id]);
            if (Array.isArray(state)) {
                return state.map((option: ISurveyOption) => ({
                    [mapping.value]: option.value,
                    [mapping.label]: option.label
                }));
            } else if (typeof state === "object") {
                return {
                    [mapping.value]: state.value,
                    [mapping.label]: state.label
                };
            }

            return state;
        };

        const formElements = this.formDefinition?.body.filter(element => element.component === "form");
        const payload: ISurveyRequest["payload"] = {
            channel: externalInterfaceStore.channel,
            templateId: externalInterfaceStore.urlDecoded?.replace(".headless.json", ""),
            templateTitle: this.headline,
            survey: flatMap(formElements, formElement =>
                formElement.items?.map(formItem => {
                    // send undefined to backend as default if there is no label ("", null)
                    const question = formItem.label || undefined;

                    if (formItem.displayMode) {
                        if (isAccountSelectionElement(formItem) || isBuildingsAccountSelectionElement(formItem)) {
                            return {
                                questionId: formItem.id,
                                type: formItem.displayMode,
                                question,
                                answer: getMappedValues(formItem, { value: "accountId", label: "account" })
                            };
                        }

                        if (isCardSelectionElement(formItem)) {
                            return {
                                questionId: formItem.id,
                                type: formItem.displayMode,
                                question,
                                answer: getMappedValues(formItem, { value: "cardId", label: "card" })
                            };
                        }

                        if (isEmailSelectionElement(formItem)) {
                            return {
                                questionId: formItem.id,
                                type: formItem.displayMode,
                                question,
                                answer: getMappedValues(formItem, { value: "emailId", label: "email" })
                            };
                        }

                        if (isPhoneSelectionElement(formItem)) {
                            return {
                                questionId: formItem.id,
                                type: formItem.displayMode,
                                question,
                                answer: getMappedValues(formItem, { value: "phoneId", label: "phone" })
                            };
                        }

                        if (isAddressSelectionElement(formItem)) {
                            return {
                                questionId: formItem.id,
                                type: formItem.displayMode,
                                question,
                                answer: getMappedValues(formItem, { value: "addressId", label: "address" })
                            };
                        }
                    }

                    return {
                        questionId: formItem.id,
                        type: formItem.displayMode ?? "TEXT",
                        question,
                        answer: getMappedValues(formItem)
                    };
                }) ?? []
            )
        };

        const body: ISurveyRequest = {
            payload,
            codEventKey: "0011",
            seAction: "survey_submitted",
            event: "customer_needs",
        };

        try {
            this._isLoading = true;

            const response = await NetbankingAPIClient.requestType(NetbankingAPI.postSurvey(body));

            this.signInfo = response.signInfo;

            this._isLoading = false;
        } catch (error) {
            this._isLoading = false;
            throw error;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/survey/SurveyStore.tsx